import { api } from "./apiClient";

// Public phone/OTP flow client (POST/GET /api/v1/public/*). The server is authoritative for
// every step; the client only keeps the scoped bearer it was issued for this browser tab
// (sessionStorage) and sends it in the Authorization header — never in a URL. No OTP value is
// ever stored, returned by the server, or rendered.

export interface InvitationView {
  status: string;
  expiresAt: string;
  /** "/join#<token>" — present only in the authenticated initiator's own view. */
  invitePath: string;
  guestState: "notOpened" | "joining" | "ready" | "away";
  delivery: string;
}

export type SessionStep = "guestPhone" | "readiness" | "waiting" | "inCall" | "ended" | "cancelled" | "expired";

export interface SessionView {
  role: "initiator" | "guest";
  step: SessionStep;
  displayName: string;
  otherPartyName: string | null;
  guestPhone: string | null;
  guestPhoneDisplay: string | null;
  invitation: InvitationView | null;
  otherPartyPresent: boolean;
  callId: number | null;
}

export interface MediaJoin {
  callId: number;
  peerId: string;
  mediaToken: string;
}

export interface StoredSession {
  token: string;
  expiresAt: string;
}

export type SessionKind = "initiator" | "guest";

const KEYS: Record<SessionKind, string> = { initiator: "vc.publicFlow", guest: "vc.publicGuest" };
const INVITE_KEY = "vc.publicInvite";

function storage(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function readStoredSession(kind: SessionKind, nowMs: number = Date.now()): StoredSession | null {
  const raw = storage()?.getItem(KEYS[kind]);
  if (!raw) return null;
  try {
    const s = JSON.parse(raw) as StoredSession;
    if (s.token && Date.parse(s.expiresAt) > nowMs) return s;
  } catch {
    // corrupt — fall through
  }
  clearStoredSession(kind);
  return null;
}

export function storeSession(kind: SessionKind, s: StoredSession): StoredSession {
  try {
    storage()?.setItem(KEYS[kind], JSON.stringify({ token: s.token, expiresAt: s.expiresAt }));
  } catch {
    // storage blocked — the session stays in memory for this page
  }
  return s;
}

export function clearStoredSession(kind: SessionKind): void {
  try {
    storage()?.removeItem(KEYS[kind]);
  } catch {
    // ignore
  }
}

/** The guest link token, kept for this tab so a refresh can re-redeem the same invitation. */
export function rememberInviteToken(token: string): void {
  try {
    storage()?.setItem(INVITE_KEY, token);
  } catch {
    // ignore
  }
}

export function readInviteToken(): string {
  try {
    return storage()?.getItem(INVITE_KEY) ?? "";
  } catch {
    return "";
  }
}

export function forgetInvite(): void {
  try {
    storage()?.removeItem(INVITE_KEY);
  } catch {
    // ignore
  }
}

// ---- API ------------------------------------------------------------------------------

/** "whatsapp": a code was sent via WhatsApp; "mock": explicit test mode (nothing sent). Never a code. */
export type OtpDelivery = "whatsapp" | "mock";

export function requestOtp(phone: string): Promise<{ challengeId: string; expiresAt: string; delivery: OtpDelivery }> {
  return api.post("/public/otp/request", { phone }, "");
}

export async function verifyOtp(challengeId: string, code: string): Promise<StoredSession & { displayName: string }> {
  const r = await api.post<StoredSession & { displayName: string }>("/public/otp/verify", { challengeId, code }, "");
  storeSession("initiator", r);
  return r;
}

export async function redeemInvitation(token: string): Promise<{ session: StoredSession; state: SessionView }> {
  const r = await api.post<StoredSession & { state: SessionView }>("/public/invitations/redeem", { token }, "");
  return { session: storeSession("guest", r), state: r.state };
}

export const getSession = (token: string) => api.get<SessionView>("/public/session", token);
export const setGuestPhone = (token: string, phone: string) => api.put<SessionView>("/public/session/guest-phone", { phone }, token);
export const markReady = (token: string) => api.post<SessionView>("/public/session/ready", undefined, token);
export const fetchMediaJoin = (token: string) => api.post<MediaJoin>("/public/session/media-token", undefined, token);
export const cancelSession = (token: string) => api.post<SessionView>("/public/session/cancel", undefined, token);
